import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { unsetClient } from '../../../actions/client';
// Components
import Box from '../../shared/atoms/Box';
import Row from '../../shared/atoms/Row';
import Input from '../../shared/atoms/Input';
import Button from '../../shared/atoms/Button';
import Alert from '../../shared/molecules/Alert';
import Spinner from '../../Spinner';
// Services
import {
  createBillingAddress,
  updateBillingAddress,
} from '../../../services/billingAddresses';
import billingAddressValidationSchema from './billingAddressValidationSchema';
import { loginUrl } from '../../../hooks/useRequireLogin';

const emptyValues = {
  billing_name: '',
  label: '',
  address_line1: '',
  postal_code: '',
  city: '',
  country: 'FR',
};

const toInitialValues = (address) =>
  address
    ? {
        billing_name: address.billing_name || '',
        label: address.label || '',
        address_line1: address.address_line1 || '',
        postal_code: address.postal_code || '',
        city: address.city || '',
        // Don't inject a "FR" default onto an existing record that had no
        // country — an empty edit must not silently rewrite it to FR.
        country: address.country || '',
      }
    : emptyValues;

const BillingAddressForm = ({
  clientId,
  mode = 'create',
  address,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(false);
    setSessionExpired(false);
    const payload = { ...values };
    if (payload.country) {
      payload.country = payload.country.toUpperCase();
    } else {
      // Omit rather than force "FR": the API defaults it on create and leaves
      // it unchanged on update.
      delete payload.country;
    }
    try {
      const { status, body: rep } =
        mode === 'edit'
          ? await updateBillingAddress(clientId, address.id, payload)
          : await createBillingAddress(clientId, payload);
      if (rep && rep.id) {
        onSuccess(rep);
      } else if (status === 401 || status === 403) {
        setSessionExpired(true);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('error saveBillingAddress', err);
      setError(true);
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={toInitialValues(address)}
      validationSchema={billingAddressValidationSchema}
      onSubmit={handleSubmit}>
      {({ isSubmitting, submitCount, errors }) => (
        <Form>
          {sessionExpired && (
            <Alert status="danger" mb={4}>
              {t('billingaddresses.alerts.session_expired')}{' '}
              {/* Session morte : vider le client du store, sinon la page de
                  connexion le croit encore connecté et renvoie ailleurs. */}
              <Link
                onClick={() => dispatch(unsetClient())}
                to={loginUrl(
                  `${window.location.pathname}${window.location.search}`,
                )}>
                {t('billingaddresses.alerts.reconnect')}
              </Link>
            </Alert>
          )}
          {error && (
            <Alert status="danger" mb={4}>
              {t('billingaddresses.alerts.error')}
            </Alert>
          )}
          <Field
            name="billing_name"
            render={({ field }) => (
              <Input
                mb={{ xs: 2, lg: 4 }}
                color="black"
                bg="white"
                label={t('billingaddresses.form.billing_name')}
                placeholder={t('billingaddresses.form.billing_name')}
                error={!!submitCount && errors.billing_name}
                {...field}
              />
            )}
          />
          <Field
            name="label"
            render={({ field }) => (
              <Input
                mb={{ xs: 2, lg: 4 }}
                color="black"
                bg="white"
                label={t('billingaddresses.form.label')}
                placeholder={t('billingaddresses.form.label.placeholder')}
                {...field}
              />
            )}
          />
          <Field
            name="address_line1"
            render={({ field }) => (
              <Input
                mb={{ xs: 2, lg: 4 }}
                color="black"
                bg="white"
                label={t('billingaddresses.form.address_line1')}
                placeholder={t('billingaddresses.form.address_line1')}
                error={!!submitCount && errors.address_line1}
                {...field}
              />
            )}
          />
          <Row sm gap={16}>
            <Field
              name="postal_code"
              render={({ field }) => (
                <Input
                  mb={{ xs: 2, lg: 4 }}
                  color="black"
                  bg="white"
                  style={{ flex: 1 }}
                  label={t('billingaddresses.form.postal_code')}
                  placeholder={t('billingaddresses.form.postal_code')}
                  error={!!submitCount && errors.postal_code}
                  {...field}
                />
              )}
            />
            <Field
              name="city"
              render={({ field }) => (
                <Input
                  mb={{ xs: 2, lg: 4 }}
                  color="black"
                  bg="white"
                  style={{ flex: 1 }}
                  label={t('billingaddresses.form.city')}
                  placeholder={t('billingaddresses.form.city')}
                  error={!!submitCount && errors.city}
                  {...field}
                />
              )}
            />
          </Row>
          <Field
            name="country"
            render={({ field }) => (
              <Input
                mb={{ xs: 2, lg: 4 }}
                color="black"
                bg="white"
                maxLength={2}
                label={t('billingaddresses.form.country')}
                placeholder="FR"
                error={!!submitCount && errors.country}
                {...field}
              />
            )}
          />
          <Box alignItems="center" justifyContent="center" mt={4}>
            <Button size="compact" variant="filled" disabled={isSubmitting}>
              {isSubmitting ? <Spinner small light /> : t('save')}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default BillingAddressForm;
